import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import FlipCameraIosIcon from "@mui/icons-material/FlipCameraIos";
import CaptureIcon from "../../../capture.svg";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Button,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import useAxiosPrivate from "../../common/useAxiosPrivate";

interface WebcamCaptureProps {
  onUploadComplete: (data: {
    towBall: string;
    passenger: string;
    driver: string;
    total: string;
  }) => void;
}

const FACING_MODE_USER = "user";
const FACING_MODE_ENVIRONMENT = "environment";

const WebcamCapture: React.FC<WebcamCaptureProps> = ({ onUploadComplete }) => {
  const webcamRef = useRef<Webcam>(null);
  const [open, setOpen] = useState(false);
  const [facingMode, setFacingMode] = useState(FACING_MODE_ENVIRONMENT);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });
  const axiosPrivate = useAxiosPrivate();

  const videoConstraints: MediaTrackConstraints = {
    facingMode: facingMode,
    width: 270,
    height: 480,
  };

  const handleSwitchCamera = () => {
    setFacingMode((prevMode) =>
      prevMode === FACING_MODE_USER ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER
    );
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCapture = async () => {
    handleClose();

    if (webcamRef.current) {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (context && webcamRef.current.video) {
        const video = webcamRef.current.video;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(async (blob) => {
          if (!blob) {
            console.error("Failed to create Blob from canvas.");
            setSnackbar({
              open: true,
              message: "Failed to capture the image.",
              severity: "error",
            });
            return;
          }

          setSnackbar({
            open: true,
            message: "Processing your image, please wait...",
            severity: "info",
          });

          const formData = new FormData();
          formData.append("file", blob, "captured-image.png");

          try {
            const response = await axiosPrivate.post(
              "/get_required_values_from_ocr",
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            if (response.status === 200 && response.data) {
              const { tow_ball, passenger, driver, total } = response.data;

              if (
                tow_ball === "0" &&
                passenger === "0" &&
                driver === "0" &&
                total === "0"
              ) {
                setSnackbar({
                  open: true,
                  message:
                    "OCR could not find any relevant data in the uploaded image. Please try again.",
                  severity: "error",
                });
              } else {
                onUploadComplete({
                  towBall: tow_ball || "0",
                  passenger: passenger || "0",
                  driver: driver || "0",
                  total: total || "0",
                });

                setSnackbar({
                  open: true,
                  message: "Image captured successfully and sent to the API.",
                  severity: "success",
                });
              }
            } else {
              console.error("Failed to process the image.");
              setSnackbar({
                open: true,
                message: "Failed to process the image. Please try again.",
                severity: "error",
              });
            }
          } catch (error) {
            console.error("Error calling OCR API:", error);
            setSnackbar({
              open: true,
              message: "Error sending data to the API.",
              severity: "error",
            });
          }
        }, "image/png");
      }
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ ml: 2 }}>
        <PhotoCameraIcon
          style={{ fontSize: "36px", color: "#6A1B9A", cursor: "pointer" }}
        />
      </IconButton>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          Capture Image
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingBottom: 0,
          }}
        >
          <Webcam
            audio={false}
            ref={webcamRef}
            screenshotFormat="image/jpeg"
            videoConstraints={videoConstraints}
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "12px",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          />
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: "16px 24px",
          }}
        >
          <Button
            variant="contained"
            onClick={handleSwitchCamera}
            startIcon={<FlipCameraIosIcon />}
            sx={{
              backgroundColor: "#E0E7FF",
              color: "#1E3A8A",
              fontWeight: "bold",
              borderRadius: "50px",
              padding: "10px 24px",
              textTransform: "capitalize",
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: "#C7D2FE",
                boxShadow: "0px 6px 10px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            Flip
          </Button>

          <Button
            variant="contained"
            onClick={handleCapture}
            startIcon={
              <img
                src={CaptureIcon}
                alt="Capture Icon"
                width="24"
                style={{
                  filter: "invert(1)",
                }}
              />
            }
            sx={{
              backgroundColor: "#6A1B9A",
              color: "#FFFFFF",
              fontWeight: "bold",
              borderRadius: "50px",
              padding: "10px 24px",
              textTransform: "capitalize",
              boxShadow: "0px 3px 6px rgba(0, 0, 0, 0.1)",
              "&:hover": {
                backgroundColor: "#4A0072",
                boxShadow: "0px 6px 10px rgba(0, 0, 0, 0.2)",
              },
            }}
          >
            Capture
          </Button>

          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderColor: "#E57373",
              color: "#E57373",
              fontWeight: "bold",
              borderRadius: "50px",
              padding: "10px 24px",
              textTransform: "capitalize",
              "&:hover": {
                backgroundColor: "#FFCDD2",
                borderColor: "#EF5350",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WebcamCapture;
